import type { CoreDepartment, CoreEmployee, UserLabel } from '@/lib/zifekCore';
import { commissionTypeMeta } from '@/lib/coreCatalog';

interface Props {
  employees: CoreEmployee[];
  departments: CoreDepartment[];
  orgNameById: Record<number, string>;
  users: Record<number, UserLabel>;
}

export default function EmployeesPanel({ employees, departments, orgNameById, users }: Props) {
  if (employees.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-16 bg-background-50 border border-background-200/70 rounded-lg">
        <div className="w-14 h-14 rounded-full bg-background-100 flex items-center justify-center mb-3">
          <i className="ri-team-line text-2xl text-foreground-400"></i>
        </div>
        <p className="text-foreground-600">Aucun employé.</p>
      </div>
    );
  }

  const deptName = (id: number | null) => departments.find((d) => d.id === id)?.name || null;

  return (
    <div className="bg-background-50 border border-background-200/70 rounded-lg overflow-hidden">
      <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[860px]">
          <thead>
            <tr className="bg-background-100 text-left text-xs text-foreground-500">
              <th className="px-4 py-3 font-medium">Employé</th>
              <th className="px-4 py-3 font-medium">Organisation</th>
              <th className="px-4 py-3 font-medium">Département</th>
              <th className="px-4 py-3 font-medium">Commission</th>
              <th className="px-4 py-3 font-medium">Statut</th>
            </tr>
          </thead>
          <tbody>
            {employees.map((e) => {
              const inactive = e.status === 'inactive';
              const u = e.user_id ? users[e.user_id] : undefined;
              const name = u?.name || e.email || `Employé #${e.id}`;
              const ct = commissionTypeMeta(e.commission_type);
              const hasCommission = Number(e.commission_value ?? 0) > 0;
              return (
                <tr key={e.id} className={`border-t border-background-200/60 ${inactive ? 'opacity-60' : ''}`}>
                  <td className="px-4 py-3">
                    <div className="min-w-0">
                      <div className="font-medium text-foreground-900 truncate">{name}</div>
                      <div className="text-xs text-foreground-400 truncate">{e.job_title || (e.user_id ? 'Compte Zifek' : 'Sans compte')}</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-foreground-600 truncate">
                    {e.organization_id ? (orgNameById[e.organization_id] || `Org #${e.organization_id}`) : '—'}
                  </td>
                  <td className="px-4 py-3 text-foreground-600 truncate">{deptName(e.department_id) || '—'}</td>
                  <td className="px-4 py-3">
                    {hasCommission ? (
                      <span className="text-[10px] px-2 py-0.5 rounded-full font-medium whitespace-nowrap bg-secondary-100 text-secondary-900">
                        {ct.key === 'fixed' ? `${e.commission_value}` : `${e.commission_value}%`}
                      </span>
                    ) : (
                      <span className="text-xs text-foreground-400">—</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`text-[10px] px-2 py-0.5 rounded-full font-medium ${inactive ? 'bg-foreground-100 text-foreground-600' : 'bg-accent-100 text-accent-900'}`}>
                      {inactive ? 'Désactivé' : 'Actif'}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}